import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { ScoringMode } from '@aiscoreboard/contracts';
import { BrandMark } from './BrandMark';
import { FullscreenToggle } from './FullscreenToggle';
import { LanguageSelect } from './LanguageSelect';
import { Toggle } from './Toggle';
import { useT } from '../lib/i18n';

interface Props {
  mode: ScoringMode;
  streaming: boolean;
  streamingPending?: boolean;
  aiUnavailableReason?: string | null;
  /** The match countdown, when one was set. Centred on the strip itself. */
  clock?: ReactNode;
  tablePreview?: ReactNode;
  onModeChange: (mode: ScoringMode) => void;
  onStreamingChange: (enabled: boolean) => void;
  onLeave: () => void;
  leaveLabel?: string;
  onRemakeFrame?: () => void;
  remakeDisabled?: boolean;
}

/**
 * The control plane. AI scoring and streaming are both switches the operator
 * owns, which is the whole point of the scoreboard being the centre of the
 * system rather than the recogniser.
 *
 * It is a thin strip along the top, deliberately quieter than everything under
 * it: these are settings touched once when the table is set up, and a row of
 * controls competing with the score for attention would be a scoreboard about
 * itself.
 */
export function ControlBar({
  mode,
  streaming,
  streamingPending = false,
  aiUnavailableReason = null,
  clock,
  tablePreview,
  onModeChange,
  onStreamingChange,
  onLeave,
  leaveLabel,
  onRemakeFrame,
  remakeDisabled = false,
}: Props) {
  const t = useT();
  const [aiNotice, setAiNotice] = useState<string | null>(null);

  return (
    <div
      className="control-bar hud-frame relative z-40 grid grid-cols-[1fr_auto_1fr] items-center gap-x-2 px-4 py-2"
      style={{
        ['--hud-bright' as string]: 'rgba(255,255,255,0.08)',
        ['--hud-glow' as string]: 'transparent',
        ['--hud-fill' as string]: 'rgba(255,255,255,0)',
        ['--hud-c' as string]: '18px',
      }}
    >
      <div className="control-settings min-w-0">
        <div className="flex items-center gap-2">
          <span className="hud-label">{t('match.stream')}</span>
          <Toggle checked={streaming} onChange={onStreamingChange} label={t('match.stream')} disabled={streamingPending} />
        </div>

        <div className="flex items-center gap-2">
          <span className="hud-label">{t('match.aiReferee')}</span>
          <Toggle
            checked={mode !== 'manual'}
            onChange={(on) => {
              if (on && aiUnavailableReason) {
                setAiNotice(aiUnavailableReason);
                return;
              }
              onModeChange(on ? 'assisted' : 'manual');
            }}
            label={t('match.aiReferee')}
            disabled={streamingPending}
          />
        </div>

      </div>

      <div className="control-center min-w-0">
        <BrandMark compact className="pointer-events-none text-center" />
        <div className="control-clock">{clock}</div>
      </div>

      {/* Right cell: fullscreen, language, leave — right-aligned as a group. */}
      <div className={`control-actions min-w-0 ${onRemakeFrame ? 'control-actions--replay' : ''} ${tablePreview ? 'control-actions--preview' : ''}`}>
        <div className="control-display-actions">
          {tablePreview}
          <FullscreenToggle />
        </div>
        <LanguageSelect compact className="min-w-[clamp(5.4rem,15vw,7rem)]" />

        <div className="control-end-actions">
          {onRemakeFrame && (
            <button type="button" title={t('match.remake')} aria-label={t('match.remake')}
              disabled={remakeDisabled} onClick={onRemakeFrame}
              className="replay-button btn-ghost text-sys-yellow">
              <span aria-hidden>↻</span>
            </button>
          )}
          <button
            type="button"
            onClick={onLeave}
            className="leave-button btn bg-sys-red/15 px-3.5 py-2 text-[0.72rem] font-bold uppercase tracking-[0.12em]
                       text-[#ff6b61] ring-1 ring-inset ring-sys-red/40
                       hover:bg-sys-red/25 hover:text-white"
          >
            {leaveLabel ?? t('leave.confirm')}
          </button>
        </div>
      </div>

      {aiNotice && <AiNotice message={aiNotice} onClose={() => setAiNotice(null)} />}
    </div>
  );
}

export function AiNotice({ message, onClose }: { message: string; onClose: () => void }) {
  const t = useT();
  const titleId = useId();
  const messageId = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  const previousFocus = useRef(document.activeElement);
  useEffect(() => {
    const element = dialog.current;
    element?.showModal();
    return () => {
      element?.close();
      const target = previousFocus.current;
      if (target instanceof HTMLElement && target.isConnected) target.focus();
    };
  }, []);

  return createPortal(
    <dialog ref={dialog} aria-labelledby={titleId} aria-describedby={messageId}
      onCancel={event => { event.preventDefault(); onClose(); }}
      className="panel fixed inset-0 m-auto max-h-[calc(100dvh-2rem)] w-[calc(100%_-_2rem)] max-w-sm overflow-y-auto border-0 bg-hud-bg/90 p-4 text-white backdrop:bg-black/75">
      <div className="flex flex-col gap-4">
        <h2 id={titleId} className="text-center text-lg font-bold">{t('match.aiReferee')}</h2>
        <p id={messageId} className="text-center text-sm text-white/70">{message}</p>
        <button type="button" onClick={onClose} className="btn-ghost min-h-11">{t('remote.close')}</button>
      </div>
    </dialog>,
    document.body,
  );
}
